import { uniqueId } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import clsx from 'clsx';
import { Field, useField } from 'formik';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { ImagePlus } from 'lucide-react';

import Button from '@dashboard/components/Button';
import Spinner from '@dashboard/components/Spinner';

import { formatBytes } from '@dashboard/services/formatting';
import logException from '@dashboard/services/logException';
import useFileQueries from '@dashboard/hooks/queries/useFileQueries';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { getImageSrcAndSrcSet } from '@dashboard/services/getUrlFromCdn';
import cropImage from '@dashboard/services/cropImage';
import { confirm } from '@dashboard/lib/confirm';

const SIZES = {
  logo: {
    width: 512,
    height: 512,
    aspect: 1,
  },
  variant_option: {
    width: 512,
    height: 512,
    aspect: 1,
  },
  favicon: {
    width: 32,
    height: 32,
    aspect: 1,
  },
  banner: {
    width: 1024,
    height: 512,
    aspect: 2,
  },
  background: {
    width: 1920,
    height: 1080,
    aspect: 2,
  },
};

const ImageUpload = ({
  image,
  name,
  label,
  onUpdate,
  crop,
  previewSize = 'logo',
  variant = 'default',
}) => {
  const { sellerId } = useAuth();
  const [file, setFile] = useState(image);
  const { uploadFile } = useFileQueries();

  useEffect(() => {
    setFile(image);
  }, [image]);

  const onDrop = useCallback(
    async acceptedFiles => {
      let acceptedFileToSet = Object.assign(acceptedFiles[0], {
        key: uniqueId(),
        url: URL.createObjectURL(acceptedFiles[0]),
        isUploading: true,
        isUploaded: false,
      });

      async function upload(file) {
        try {
          const { data, error } = await uploadFile({
            file,
            bucket: 'images',
            sellerId,
          });

          if (error) {
            throw error;
          }

          const mediaItem = {
            key: file.key,
            file: data._id,
            url: data.location,
            name: data.name,
            size: data.size,
            width: data.width,
            height: data.height,
          };

          setFile(mediaItem);
          onUpdate(mediaItem);
        } catch (error) {
          console.log(error);
          logException({ message: 'Failed to upload file', error });
          setFile(null);
        }
      }

      let img = new Image();
      img.src = window.URL.createObjectURL(acceptedFileToSet);
      img.onload = async () => {
        acceptedFileToSet.width = img.width;
        acceptedFileToSet.height = img.height;

        if (crop && acceptedFileToSet.type !== 'image/gif') {
          const croppedFile = await cropImage(img, crop, acceptedFileToSet);

          const existingFile = acceptedFileToSet;

          acceptedFileToSet = croppedFile;
          acceptedFileToSet.height = acceptedFileToSet.height || existingFile.height;
          acceptedFileToSet.width = acceptedFileToSet.width || existingFile.width;
          acceptedFileToSet.isUploaded = existingFile.isUploaded;
          acceptedFileToSet.isUploading = existingFile.isUploading;
          acceptedFileToSet.key = existingFile.key;
          acceptedFileToSet.path = existingFile.path;
          acceptedFileToSet.url = URL.createObjectURL(croppedFile);
        }

        setFile(acceptedFileToSet);

        await upload(acceptedFileToSet);
      };
    },
    [file],
  );

  const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
    onDrop,
    maxFiles: 1,
    multiple: false,
    accept: 'image/jpeg, image/png, image/jpg, image/gif, image/webp, image/svg+xml',
    maxSize: 10000000,
  });

  async function onDeleteFile() {
    setFile(null);
    onUpdate(null);
  }

  return (
    <div className="h-full w-full">
      {file && (
        <File
          file={file}
          onDeleteFile={() => onDeleteFile(file, file.file)}
          previewSize={previewSize}
          isLast
          variant={variant}
        />
      )}

      {!file && (
        <div
          {...getRootProps()}
          className={clsx(
            'transition-colors border border-dashed border-input rounded-md cursor-pointer bg-background hover:bg-accent/40 hover:border-ring focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            {
              'border-solid border-ring bg-accent/40': isDragActive,
              'h-full w-full': variant !== 'design-form',
              'w-16 h-16': variant === 'design-form',
            },
          )}
        >
          <input name={name} id={name} {...getInputProps()} />

          <div
            className={clsx({
              'flex items-center justify-start gap-3 p-4': variant === 'default',
              'h-full w-full flex items-center justify-center':
                variant === 'minimal' || variant === 'design-form',
            })}
          >
            <div
              className={clsx(
                'flex items-center justify-center rounded-md bg-muted text-muted-foreground shrink-0',
                {
                  'h-9 w-9': variant === 'default',
                  'h-8 w-8': variant === 'minimal' || variant === 'design-form',
                },
              )}
            >
              <ImagePlus
                aria-hidden="true"
                strokeWidth={1.75}
                className={clsx({
                  'h-4 w-4': variant === 'default',
                  'h-4 w-4': variant === 'minimal' || variant === 'design-form',
                })}
              />
            </div>

            {variant === 'default' && (
              <div className="space-y-0.5">
                <div className="text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">Upload image</span> or drag and drop
                </div>
                <p className="text-xs text-muted-foreground">
                  Recommended size is {SIZES[previewSize].width} × {SIZES[previewSize].height}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {fileRejections.map(({ file, errors }) => (
        <FileRejection key={file.path} file={file} errors={errors} />
      ))}
    </div>
  );
};

function FileRejection({ file, errors }) {
  if (!file.type.startsWith('image/')) {
    return (
      <div className="my-1 text-destructive" key={file.path}>
        {file.name} is not an image
      </div>
    );
  }

  if (file.size > 10000000) {
    return (
      <div className="my-1 text-destructive">
        {`${file.name} is too big (${formatBytes(file.size)}). Max size: 10MB`}
      </div>
    );
  }

  return errors.map(err => (
    <div key={err.key} className="my-1 text-destructive">
      {err.message}
    </div>
  ));
}

function File({ file, onDeleteFile, isLast, previewSize, variant }) {
  async function onDelete() {
    const del = await confirm('Delete this image?', {
      confirm: 'Delete',
    });

    if (del) {
      try {
        await onDeleteFile(file);
      } catch (error) {
        window.alert('Unable to delete image');
      }
    }
  }

  const fileImage = (() => {
    if (file.url.includes('blob:')) {
      return { src: file.url, srcSet: undefined };
    }

    const { width, height } = (() => {
      switch (previewSize) {
        case 'background':
        case 'variant_option': {
          return {
            width: 64,
            height: 64,
          };
        }
        case 'ticket_type': {
          return {
            width: 71,
            height: 71,
          };
        }
        case 'banner': {
          return {
            width: 256,
            height: 128,
          };
        }
        case 'logo': {
          return {
            width: 80,
            height: 80,
          };
        }
        case 'favicon': {
          return {
            height: 24,
            width: 24,
          };
        }
        default: {
          return {
            width: 64,
            height: 64,
          };
        }
      }
    })();

    return getImageSrcAndSrcSet({ url: file.url, width, height });
  })();

  return (
    <div
      className={clsx(`${!isLast ? 'border-b' : ''}`, {
        'mb-4 flex flex-row gap-3': variant === 'default',
        'h-full w-full flex items-center justify-center': variant === 'minimal',
      })}
    >
      <div
        className={clsx('relative', {
          'overflow-hidden': variant !== 'design-form',
          'rounded-full shadow w-20 h-20 flex items-center justify-center':
            previewSize === 'logo' || previewSize === 'favicon',
          'flex-grow-0 flex-shrink-0': variant === 'default',
          'w-full h-full': variant === 'minimal',
          'w-16 h-16 rounded-md border border-gray-200 dark:border-border bg-gray-50 dark:bg-card':
            variant === 'design-form',
        })}
      >
        {variant === 'design-form' ? (
          <div className="absolute inset-0 overflow-hidden rounded-md">
            <img
              className="absolute inset-0 w-full h-full object-cover"
              src={fileImage.src}
              srcSet={fileImage.srcSet}
              alt={file.name}
            />

            {file.isUploading && (
              <div className="absolute inset-0 flex items-center justify-center bg-background/50">
                <Spinner width="24px" height="24px" />
              </div>
            )}
          </div>
        ) : (
          <img
            className={clsx('text-transparent rounded-md object-cover', {
              'w-16 h-16': previewSize === 'background' || previewSize === 'variant_option',
              'w-[71px] h-[71px]': previewSize === 'ticket_type',
              'w-64 h-32': previewSize === 'banner',
              'w-20 h-20': previewSize === 'logo',
              'w-6 h-6': previewSize === 'favicon',
              'dark:ring-1 dark:ring-foreground/25':
                previewSize === 'logo' || previewSize === 'favicon',
            })}
            src={fileImage.src}
            srcSet={fileImage.srcSet}
            alt={file.name}
          />
        )}

        {variant === 'minimal' && file.isUploading && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/50">
            <Spinner width="24px" height="24px" />
          </div>
        )}

        {variant === 'minimal' && (
          <div className="absolute top-px right-px flex items-center justify-center">
            <button
              onClick={onDelete}
              aria-label="delete"
              type="button"
              className="rounded-full p-px bg-muted/60 hover:bg-muted text-foreground transition-colors"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>
        )}

        {variant === 'design-form' && (
          <button
            onClick={onDelete}
            aria-label="Remove image"
            type="button"
            className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-md bg-white dark:bg-background text-gray-600 dark:text-muted-foreground shadow-sm ring-1 ring-gray-200 dark:ring-border transition-colors hover:bg-gray-50 dark:hover:bg-muted hover:text-gray-900 dark:hover:text-foreground"
          >
            <XMarkIcon className="h-2.5 w-2.5" strokeWidth={2.25} />
          </button>
        )}
      </div>

      {variant === 'default' && (
        <div className="flex flex-col gap-2">
          {file.isUploading && <Spinner width="24px" height="24px" />}

          <div className="mt-auto flex gap-2">
            <Button appearance="subtle" onClick={onDelete} aria-label="delete" type="button">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                height="16px"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export const ImageField = props => {
  // eslint-disable-next-line no-unused-vars
  const [field, meta, helpers] = useField(props);

  const { setValue } = helpers;

  const onChange = value => {
    setValue(value);

    if (props.onChange) {
      props.onChange(value);
    }
  };

  return (
    <Field {...props} as={ImageUpload} onUpdate={onChange} image={field.value} name={field.name} />
  );
};

export default ImageUpload;
